import React, { useEffect, useState } from 'react';
import { Alert, ProgressBar, Spinner, Table } from 'react-bootstrap';
import { useAuth } from '../../context/AuthContext';
import accountApi from '../../services/accountApi';
import StatusBadge from '../../components/StatusBadge';
import EmptyState from '../../components/EmptyState';

function formatBytes(n) {
  if (n == null) return '—';
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  if (v < 1024) return `${v} B`;
  if (v < 1024 * 1024) return `${(v / 1024).toFixed(1)} KB`;
  if (v < 1024 * 1024 * 1024) return `${(v / (1024 * 1024)).toFixed(1)} MB`;
  return `${(v / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function UsagePage() {
  const { activeTenantId } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!activeTenantId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const payload = await accountApi.getPartnerUsage(activeTenantId);
        if (!cancelled) setData(payload);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load usage');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeTenantId]);

  const ents = data?.entitlements;
  const usage = data?.usage;
  const quotas = data?.quotas || [];

  return (
    <div>
      <h1 className="product-page-title">Usage & entitlements</h1>
      <p className="product-page-subtitle">
        Operational usage and plan limits. Slice 8 does not include billing.
      </p>

      {error && <Alert variant="danger">{error}</Alert>}
      {loading && (
        <div className="loading-state">
          <Spinner animation="border" size="sm" className="me-2" />
          Loading…
        </div>
      )}

      {!loading && !data && !error && (
        <EmptyState title="No usage data" description="Select an active Partner." />
      )}

      {ents && (
        <div className="product-card mb-3">
          <h2 className="h6">Plan & capabilities</h2>
          <p className="mb-1">
            <strong>Bundle:</strong> {ents.capability_bundle}{' '}
            <span className="text-muted small">v{ents.bundle_version}</span>
          </p>
          <p className="mb-1">
            <strong>Status:</strong> <StatusBadge status={ents.status} />
          </p>
          <p className="mb-1">
            <strong>Service tier:</strong> {ents.service_tier || '—'}
          </p>
          <p className="mb-2 text-muted small">
            Capability bundles are product entitlements and do not replace role permissions.
            Contact the platform team to change your plan.
          </p>
          <div className="d-flex flex-wrap gap-1">
            {(ents.capabilities || []).map((c) => (
              <span key={c} className="badge text-bg-secondary">
                {c}
              </span>
            ))}
          </div>
        </div>
      )}

      {quotas.length > 0 && (
        <div className="product-card mb-3">
          <h2 className="h6">Quotas</h2>
          <Table responsive size="sm" className="product-table">
            <thead>
              <tr>
                <th>Quota</th>
                <th>Current</th>
                <th>Limit</th>
                <th>Remaining</th>
                <th>Status</th>
                <th style={{ minWidth: 120 }}>Usage</th>
              </tr>
            </thead>
            <tbody>
              {quotas.map((q) => (
                <tr key={q.code}>
                  <td className="mono small">{q.code}</td>
                  <td>{q.current ?? '—'}</td>
                  <td>{q.limit ?? '—'}</td>
                  <td>{q.remaining ?? '—'}</td>
                  <td>
                    <StatusBadge status={q.status === 'ok' ? 'active' : q.status} />
                    {!q.enforced ? (
                      <div className="small text-muted">informational</div>
                    ) : null}
                  </td>
                  <td>
                    {q.percent_used != null ? (
                      <ProgressBar
                        now={Math.min(100, q.percent_used)}
                        label={`${q.percent_used}%`}
                        variant={
                          q.status === 'exceeded'
                            ? 'danger'
                            : q.status === 'warning'
                              ? 'warning'
                              : 'success'
                        }
                      />
                    ) : (
                      '—'
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}

      {usage && (
        <div className="product-card mb-3">
          <h2 className="h6">Current usage</h2>
          <div className="row g-3">
            {[
              ['Active projects', usage.projects_active],
              ['Queued jobs', usage.queued_jobs],
              ['Running jobs', usage.running_jobs],
              ['Completed jobs', usage.completed_jobs],
              ['Failed jobs', usage.failed_jobs],
              ['Jobs this month', usage.jobs_this_month],
              ['Execution minutes (month)', usage.execution_minutes_this_month],
              ['Artifacts', usage.artifact_count],
              ['Recorded artifact storage', formatBytes(usage.artifact_bytes_recorded)],
              ['Recorded revision archives', formatBytes(usage.revision_archive_bytes_recorded)],
              ['Active API keys', usage.api_keys_active],
              ['Active members', usage.members_active],
              ['Pending invitations', usage.invitations_pending],
              ['Compat analyses (month)', usage.compatibility_analyses_this_month],
            ].map(([label, value]) => (
              <div className="col-6 col-md-3" key={label}>
                <div className="text-muted small">{label}</div>
                <div className="fs-5">{value ?? '—'}</div>
              </div>
            ))}
          </div>
          <p className="small text-muted mt-3 mb-0">{usage.execution_note}</p>
          <p className="small text-muted mb-0">{usage.storage_note}</p>
        </div>
      )}
    </div>
  );
}

export default UsagePage;
